import type { AdminUserListItem } from '@nestjs-boilerplate/types';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { type DataCardConfig, DataCardList } from '@/components/ui/data-card-list';
import { DataTable, sortableHeader } from '@/components/ui/data-table';
import { DateFilterInput } from '@/components/ui/date-filter-input';
import { Label } from '@/components/ui/label';
import { MobileSortControl, type MobileSortOption } from '@/components/ui/mobile-sort-control';
import { MultiSelect } from '@/components/ui/multi-select';
import { Skeleton } from '@/components/ui/skeleton';
// Cross-feature imports: the established pattern here, and cheaper than
// relocating two widgets the dashboard already owns.
import { Pagination } from '@/features/dashboard/components/pagination';
import { SearchInput } from '@/features/dashboard/components/search-input';
import { useIsMobile } from '@/hooks/use-is-mobile';
import type { AdminUserFilters } from '../api/users';
import { useAdminUsers } from '../api/users';
import { formatBalance, formatDate, statusOf } from './user-display';

/**
 * Roles the filter offers.
 *
 * An empty selection is the "no filter" state, so there is no "Any role" entry
 * to select — clearing the control is how a reader widens the list again.
 */
const ROLES = [
  { value: 'user', label: 'User' },
  { value: 'admin', label: 'Admin' },
  { value: 'superadmin', label: 'Superadmin' },
];

/** Statuses the filter offers, in the order the badge ranks them. */
const USER_STATES = [
  { value: 'active', label: 'Active' },
  { value: 'pending_review', label: 'Pending review' },
  { value: 'unverified', label: 'Unverified' },
  { value: 'setup_incomplete', label: 'Setup incomplete' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'deleted', label: 'Deleted' },
];

/**
 * What the server orders by when the request names no column.
 *
 * Held here so the header indicator can show the ordering that is actually in
 * effect. It is deliberately not sent: an explicit `sortBy=createdAt` would
 * make the URL claim a choice the reader never made.
 */
const DEFAULT_SORTING: SortingState = [{ id: 'createdAt', desc: true }];

/**
 * Sort options offered on mobile.
 *
 * The same five columns the table exposes a sortable header for, which is the
 * subset of the API's sort columns this screen has a column to sort.
 */
const MOBILE_SORT_OPTIONS: MobileSortOption[] = [
  { id: 'name', label: 'Name', descLabel: 'Z → A', ascLabel: 'A → Z' },
  { id: 'company', label: 'Company', descLabel: 'Z → A', ascLabel: 'A → Z' },
  { id: 'role', label: 'Role', descLabel: 'Z → A', ascLabel: 'A → Z' },
  { id: 'balance', label: 'Balance', descLabel: 'Highest first', ascLabel: 'Lowest first' },
  { id: 'createdAt', label: 'Joined', descLabel: 'Newest first', ascLabel: 'Oldest first' },
];

/** The one status worth showing for a user, as a badge. */
function StatusBadge({ user }: { user: AdminUserListItem }) {
  const status = statusOf(user);
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${status.className}`}>
      {status.label}
    </span>
  );
}

/**
 * The user's name as a link to their record.
 *
 * Kept as a real anchor alongside the row's click handler rather than replaced
 * by it. The handler gives the whole row as a target; the anchor is what keeps
 * middle-click and open-in-new-tab working, which a click handler cannot
 * serve, and gives screen readers a named link within the row.
 */
function UserNameLink({ user, className }: { user: AdminUserListItem; className: string }) {
  return (
    <Link
      to="/admin/users/$userId"
      params={{ userId: user.id }}
      className={className}
      // The row is clickable too. Without this the anchor navigates and then
      // the row handler navigates again to the same place, pushing a
      // duplicate history entry that makes Back appear not to work.
      onClick={(event) => event.stopPropagation()}
    >
      {user.fullName ?? user.email}
    </Link>
  );
}

interface AdminUsersPageProps {
  filters: AdminUserFilters;
  onFiltersChange: (next: AdminUserFilters) => void;
}

/**
 * Admin-scoped list of every platform user.
 *
 * Filter state is owned by the route and passed in, so this component needs no
 * router to be tested and the URL stays the single source of truth.
 */
export function AdminUsersPage({ filters, onFiltersChange }: AdminUsersPageProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  // Whole-row navigation. The name stays a real <Link> as well: the row
  // handler cannot serve middle-click or open-in-new-tab, so removing the
  // anchor would take those away to gain a larger target. Keeping both means
  // the row is easy to hit and the link still behaves like a link.
  const openUser = useCallback(
    (row: AdminUserListItem) => {
      navigate({ to: '/admin/users/$userId', params: { userId: row.id } });
    },
    [navigate],
  );

  // Optional chaining, not direct destructuring: the query result is never
  // actually undefined in production, but guarding here costs nothing and
  // keeps this component from depending on TanStack Query's return shape
  // never changing.
  const query = useAdminUsers(filters);
  const data = query?.data;
  const isLoading = query?.isLoading ?? false;
  const isError = query?.isError ?? false;
  const error = query?.error ?? null;
  const refetch = query?.refetch;

  const update = useCallback(
    (patch: Partial<AdminUserFilters>) => {
      // Any change to the filter set invalidates the current page number: page 4
      // of the old result set is very likely past the end of the new one.
      const resetsPage = !('page' in patch);
      onFiltersChange({ ...filters, ...patch, ...(resetsPage ? { page: 1 } : {}) });
    },
    [filters, onFiltersChange],
  );

  // Falls back to the server's own default rather than to no sort at all, so
  // the Joined header does not show as unsorted while the rows are in fact
  // ordered by it.
  const sorting: SortingState = filters.sortBy
    ? [{ id: filters.sortBy, desc: filters.sortOrder !== 'asc' }]
    : DEFAULT_SORTING;

  const onSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      update(
        first
          ? {
              sortBy: first.id as AdminUserFilters['sortBy'],
              sortOrder: first.desc ? 'desc' : 'asc',
            }
          : { sortBy: undefined, sortOrder: undefined },
      );
    },
    [sorting, update],
  );

  const columns = useMemo<ColumnDef<AdminUserListItem>[]>(
    () => [
      {
        id: 'name',
        header: sortableHeader<AdminUserListItem>('Name'),
        cell: ({ row }) => (
          <div>
            <UserNameLink user={row.original} className="font-medium hover:underline" />
            {row.original.fullName ? (
              <div className="text-xs text-muted-foreground">{row.original.email}</div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'company',
        header: sortableHeader<AdminUserListItem>('Company'),
        cell: ({ row }) => row.original.companyName ?? '—',
      },
      {
        id: 'role',
        header: sortableHeader<AdminUserListItem>('Role'),
        cell: ({ row }) => row.original.roleName,
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => <StatusBadge user={row.original} />,
      },
      {
        id: 'assessments',
        header: 'Assessments',
        enableSorting: false,
        cell: ({ row }) => row.original.assessmentCount,
      },
      {
        id: 'balance',
        header: sortableHeader<AdminUserListItem>('Balance'),
        cell: ({ row }) => formatBalance(row.original.balance),
      },
      {
        id: 'createdAt',
        header: sortableHeader<AdminUserListItem>('Joined'),
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
    ],
    [],
  );

  // The same seven columns as cards, because seven columns on a phone is a
  // horizontal scroll nobody reads.
  const cardConfig = useMemo<DataCardConfig<AdminUserListItem>>(
    () => ({
      // Tapping the card opens the record, mirroring the row click on the
      // table. Without this the two layouts disagree: the same user is
      // reachable by tapping anywhere on desktop and only by hitting the name
      // on a phone, which is the smaller target on the smaller screen.
      onClick: openUser,
      header: (row) => (
        <div className="flex flex-col gap-0.5">
          <UserNameLink user={row} className="font-semibold hover:underline" />
          {row.fullName ? <span className="text-sm text-muted-foreground">{row.email}</span> : null}
        </div>
      ),
      fields: [
        { label: 'Company', render: (row) => row.companyName ?? '—' },
        { label: 'Role', render: (row) => row.roleName },
        { label: 'Status', render: (row) => <StatusBadge user={row} /> },
        { label: 'Assessments', render: (row) => row.assessmentCount },
        { label: 'Balance', render: (row) => formatBalance(row.balance) },
        { label: 'Joined', render: (row) => formatDate(row.createdAt) },
      ],
    }),
    [openUser],
  );

  // Laid out to match the activity page: a Card of labelled controls on the
  // same responsive grid. The two screens sit next to each other in the
  // sidebar, and an unlabelled inline bar on one and a labelled card on the
  // other read as two different products.
  const filterBar = (
    <Card>
      <CardContent className="grid gap-4 pt-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-2">
          <Label htmlFor="search">Search</Label>
          <SearchInput
            id="search"
            value={filters.search ?? ''}
            onChange={(search) => update({ search })}
            placeholder="Name, email or company"
            className="w-full"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="role">Role</Label>
          <MultiSelect
            id="role"
            value={filters.role ?? []}
            onChange={(role) => update({ role: role.length > 0 ? role : undefined })}
            options={ROLES}
            placeholder="Any role"
          />
        </div>

        <div className="space-y-2">
          {/* "Status", matching the column it filters. The URL parameter stays
              `state` — it is validated in three places and the reader never
              sees it, so renaming it would be churn for no visible gain. */}
          <Label htmlFor="state">Status</Label>
          <MultiSelect
            id="state"
            value={filters.state ?? []}
            onChange={(state) =>
              update({
                state: state.length > 0 ? (state as AdminUserFilters['state']) : undefined,
              })
            }
            options={USER_STATES}
            placeholder="Any status"
          />
        </div>

        {/* Both ends inclusive, so picking the same day twice returns everyone
            who joined that day. Cleared inputs send undefined rather than an
            empty string, which would reach the API as an invalid date. The pair
            is cross-bounded, so the calendar cannot offer a range that reads
            backwards and returns nothing. */}
        <div className="space-y-2">
          <Label htmlFor="joined-from">Joined from</Label>
          <DateFilterInput
            id="joined-from"
            value={filters.from}
            onChange={(from) => update({ from })}
            max={filters.to ? new Date(filters.to) : undefined}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="joined-to">Joined to</Label>
          <DateFilterInput
            id="joined-to"
            value={filters.to}
            onChange={(to) => update({ to })}
            min={filters.from ? new Date(filters.from) : undefined}
          />
        </div>
      </CardContent>
    </Card>
  );

  // The filter bar renders in every state, including the failed one. Returning
  // early on an error left the reader with no control but the address bar —
  // and a bad filter in the URL is the likeliest way to get here.
  function body() {
    if (isError) {
      return (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <p className="text-sm text-destructive">
              Could not load users
              {error instanceof Error ? `: ${error.message}` : '.'}
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch?.()}>
              Try again
            </Button>
          </CardContent>
        </Card>
      );
    }

    if (isLoading) {
      return (
        <div data-testid="admin-users-loading" className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      );
    }

    if (data && data.items.length === 0) {
      return (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">No users match these filters</p>
          </CardContent>
        </Card>
      );
    }

    // Mobile keeps its own stacked layout: the cards are already separate
    // surfaces, so wrapping them in one more card would nest a card in a card.
    if (isMobile) {
      return (
        <>
          <MobileSortControl
            options={MOBILE_SORT_OPTIONS}
            value={sorting[0] ? { id: sorting[0].id, desc: sorting[0].desc } : undefined}
            onChange={(next) => {
              onSortingChange([{ id: next.id, desc: next.desc }]);
            }}
          />
          <DataCardList<AdminUserListItem>
            data={data?.items ?? []}
            getRowId={(row) => row.id}
            cardConfig={cardConfig}
            emptyMessage="No users match these filters"
          />
          <Pagination
            page={data?.page ?? 1}
            pageSize={data?.pageSize ?? 20}
            total={data?.total ?? 0}
            totalPages={data?.totalPages ?? 0}
            onPageChange={(page) => update({ page })}
            onPageSizeChange={(pageSize) => update({ pageSize })}
          />
        </>
      );
    }

    // Table and pagination share one Card, the pagination divided off by a
    // rule rather than floating on the page background. Matches the activity
    // page, which is the pattern the admin section already established.
    return (
      <Card>
        <DataTable
          columns={columns}
          data={data?.items ?? []}
          getRowId={(row) => row.id}
          sorting={sorting}
          onSortingChange={onSortingChange}
          onRowClick={openUser}
          unframed
        />
        <div className="border-t p-4">
          <Pagination
            page={data?.page ?? 1}
            pageSize={data?.pageSize ?? 20}
            total={data?.total ?? 0}
            totalPages={data?.totalPages ?? 0}
            onPageChange={(page) => update({ page })}
            onPageSizeChange={(pageSize) => update({ pageSize })}
          />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">User Management</h1>
      {filterBar}
      {body()}
    </div>
  );
}
